import { Alert } from "@mui/material";
import {
  BooleanField,
  DataTable,
  DateTimeInput,
  ExportButton,
  FilterButton,
  List,
  ReferenceField,
  SelectArrayInput,
  TextField,
  TopToolbar,
  useNotify,
  usePermissions,
  useRecordContext,
} from "react-admin";
import {
  billingListDefaultValues,
  exportToExcelFacturante,
} from "../utils/BillingUtils";
import { getCountries } from "../enums/Countries";
import { validateRequired } from "../utils/Validations";
import { ROLE_TYPES } from "../enums/Roles";
import { PriceField } from "../common/PriceField";

const parseBillingDate = (value, endOfMinute = false) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  if (endOfMinute) date.setSeconds(59, 999);
  return date.toISOString();
};

const parseBillingDateFrom = (value) => parseBillingDate(value);
const parseBillingDateTo = (value) => parseBillingDate(value, true);

const CurrencyNumberField = ({ source, label }) => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <PriceField
      source={source}
      country={record.restaurantCountry}
      label={label}
    />
  );
};

const adminExporter = (billingInfo, notify) => {
  try {
    let paidDateFrom = null;
    let paidDateTo = null;
    const filters = localStorage.getItem("RaStore.billing.listParams");
    if (filters) {
      const { filter } = JSON?.parse(filters);
      if (filter) {
        paidDateFrom = filter.paidDateFrom;
        paidDateTo = filter.paidDateTo;
      }
    }

    // If dates are not available, avoid exporting with incorrect period
    if (!paidDateFrom || !paidDateTo) {
      return notify(
        "No se pudo determinar el período de pago para la exportación.",
        {
          type: "error",
        }
      );
    }

    exportToExcelFacturante({ billingInfo, paidDateFrom, paidDateTo });
  } catch (e) {
    notify(
      <Alert severity="error">Error al exportar los datos de facturación</Alert>
    );
  }
};

const BillingFilters = () => {
  return [
    <SelectArrayInput
      label="País"
      source="country"
      choices={getCountries()}
      validate={validateRequired}
      alwaysOn={true}
    />,
    <DateTimeInput
      source="paidDateFrom"
      label="Fecha Pago Desde"
      parse={parseBillingDateFrom}
      alwaysOn={true}
      validate={validateRequired}
    />,
    <DateTimeInput
      source="paidDateTo"
      label="Fecha Pago Hasta"
      parse={parseBillingDateTo}
      alwaysOn={true}
      validate={validateRequired}
    />,
  ];
};

const BillingListActions = () => {
  const { permissions } = usePermissions();
  const notify = useNotify();

  return (
    <TopToolbar>
      <FilterButton />
      {permissions === ROLE_TYPES.ADMIN && (
        <ExportButton
          maxResults={99999}
          exporter={(data) => adminExporter(data, notify)}
        />
      )}
    </TopToolbar>
  );
};

export default function BillingList() {
  const { paidDateFrom, paidDateTo, country } = billingListDefaultValues();

  return (
    <List
      actions={<BillingListActions />}
      filterDefaultValues={{
        paidDateFrom,
        paidDateTo,
        country,
      }}
      filters={BillingFilters()}
    >
      <BillingListComponent />
    </List>
  );
}

function BillingListComponent() {
  return (
    <DataTable bulkActionButtons={false}>
      <DataTable.Col source="id" label="Nombre local">
        <ReferenceField
          reference="restaurants"
          source="id"
          label="Nombre local"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>

      <DataTable.Col source="totalPaidAmount" label="Total pagado">
        <CurrencyNumberField source="totalPaidAmount" label="Total pagado" />
      </DataTable.Col>
      <DataTable.Col source="totalRefunds" label="Total reembolsado">
        <CurrencyNumberField source="totalRefunds" label="Total reembolsado" />
      </DataTable.Col>
      {/* <CurrencyNumberField
        source="totalDiscountCouponMorfy"
        label="Cupon Morfy"
      /> */}
      <DataTable.Col source="netMorfyAmount" label="Neto Morfy (sin IVA)">
        <CurrencyNumberField
          source="netMorfyAmount"
          label="Neto Morfy (sin IVA)"
        />
      </DataTable.Col>
      {/* <CurrencyNumberField
        source="totalDiscountCouponRestaurant"
        label="Cupon Restaurante"
      /> */}
      <DataTable.Col source="netRestaurantAmount" label="Neto Restaurant">
        <CurrencyNumberField
          source="netRestaurantAmount"
          label="Neto Restaurant"
        />
      </DataTable.Col>
      <DataTable.Col
        source="totalServiceCostAmount"
        label="Costo de servicio (IVA incl.)"
      >
        <CurrencyNumberField
          source="totalServiceCostAmount"
          label="Costo de servicio (IVA incl.)"
        />
      </DataTable.Col>
      <DataTable.Col source="billingData" label="Datos de Facturación">
        <BooleanField
          source="billingData"
          label="Datos de Facturación"
          looseValue
        />
      </DataTable.Col>
    </DataTable>
  );
}
